import { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import FYSelect from "@/components/FYSelect";
import { loadFinancialYears, fyLabel } from "@/lib/fy";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function TNIMatrix({ workforceType = null }) {
  const [data, setData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filterDept, setFilterDept] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [fy, setFy] = useState(null);

  useEffect(() => {
    loadFinancialYears().then((d) => setFy((prev) => prev ?? d.current_fy));
  }, []);

  useEffect(() => {
    if (fy === null) return;
    const params = { fy, ...(workforceType ? { workforce_type: workforceType } : {}) };
    api.get("/matrix/tni", { params }).then((r) => setData(r.data));
    api.get("/employees", { params: workforceType ? { workforce_type: workforceType } : {} }).then((r) => setEmployees(r.data));
  }, [workforceType, fy]);

  const depts = Array.from(new Set(employees.map((e) => e.department))).filter(Boolean);

  const filtered = data?.employees.filter((e) => !filterDept || e.department === filterDept) || [];

  const exportExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filterDept) params.set("department", filterDept);
      if (workforceType) params.set("workforce_type", workforceType);
      if (fy !== null) params.set("fy", String(fy));
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await fetch(`${API}/matrix/tni/excel${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `TNIMatrix_${new Date().toISOString().slice(0, 10)}.xlsx`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel downloaded successfully");
    } catch (e) {
      toast.error("Export failed — please try again");
    } finally {
      setDownloading(false);
    }
  };

  const trackLabel = workforceType === "worker" ? "Operators" : workforceType === "staff" ? "Staff" : null;
  const title = trackLabel ? `TNI Matrix — ${trackLabel}` : "TNI Matrix";
  const eyebrow = trackLabel ? `TNI · ${trackLabel}` : "Training Need Identification";
  const breadcrumb = trackLabel ? `TNI Matrix — ${trackLabel}` : "TNI Matrix";

  return (
    <div data-testid="tni-matrix-page">
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={`Organization-wide gap view across every competency. Red cells = identified gaps. Showing results for ${fyLabel(fy)}.`}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: breadcrumb }]}
        action={
          <div className="flex items-end gap-2">
            <FYSelect value={fy} onChange={setFy} testid="tni-fy-select" />
            <select
              data-testid="tni-dept-filter"
              value={filterDept}
              onChange={(e) => setFilterDept(e.target.value)}
              className="border border-[#E5E5E5] rounded-sm px-3 py-2 text-sm bg-white"
            >
              <option value="">All departments</option>
              {depts.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <button
              type="button"
              onClick={exportExcel}
              disabled={downloading || !data}
              data-testid="tni-export-excel"
              className="inline-flex items-center gap-1.5 bg-[#0D0D0D] hover:bg-neutral-800 text-white text-sm rounded-sm px-3 py-2 disabled:opacity-50"
            >
              {downloading ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />}
              {downloading ? "Exporting…" : "Export Excel"}
            </button>
          </div>
        }
      />
      <div className="px-4 md:px-10 py-8">
        <div className="bc-card overflow-x-auto">
          {!data ? <div className="p-10 text-sm text-[#666666]">Loading…</div> : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-[#F9F9F9] border-b-2 border-[#0D0D0D]">
                  <th className="text-left px-3 py-2 bc-label sticky left-0 bg-[#F9F9F9] z-10">Employee</th>
                  <th className="text-left px-3 py-2 bc-label">Dept</th>
                  {data.skills.map((s) => (
                    <th key={s.code} className="px-2 py-2 bc-label text-center" title={s.name}>
                      <div className="font-mono">{s.code}</div>
                      <div className="font-normal text-[9px] text-[#666666] normal-case tracking-normal mt-0.5 max-w-[80px] truncate" title={s.name}>{s.name}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp) => (
                  <tr key={emp.employee_id} className="border-b border-[#E5E5E5]" data-testid={`tni-row-${emp.employee_id}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white font-medium z-10">
                      {emp.name}
                      <div className="text-[10px] text-[#666666] font-normal">#{emp.employee_id}</div>
                    </td>
                    <td className="px-3 py-2 text-[#666666]">{emp.department}</td>
                    {data.skills.map((s) => {
                      const c = emp.cells[s.code];
                      const cls = `cell-${c.status.replace("_", "-")}`;
                      return (
                        <td key={s.code} className={`text-center px-2 py-2 font-mono ${cls}`}>
                          {c.status === "not_required" ? "" : c.achieved ? c.achieved : "—"}
                          {c.status === "gap" && c.required && (
                            <div className="text-[9px] mt-0.5 opacity-70">need {c.required}</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex gap-4 mt-4 text-xs">
          <span><span className="inline-block w-3 h-3 mr-1 align-middle cell-competent border" /> Competent</span>
          <span><span className="inline-block w-3 h-3 mr-1 align-middle cell-gap border" /> Gap</span>
          <span><span className="inline-block w-3 h-3 mr-1 align-middle cell-not-assessed border" /> Not assessed</span>
        </div>
      </div>
    </div>
  );
}
